'use client';

import { useState } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import {
  resolveWeatherLabels,
  type WeatherLabels,
} from '@/components/weather/labels';

const tabs = [
  { labelKey: 'temp', value: 'temp' },
  { labelKey: 'wind', value: 'wind' },
  { labelKey: 'snow', value: 'snowcover' },
  { labelKey: 'rain', value: 'rain' },
  { labelKey: 'clouds', value: 'clouds' },
] as const;

type TabValue = (typeof tabs)[number]['value'];
type TabLabelKey = (typeof tabs)[number]['labelKey'];

interface WindyMapProps {
  className?: string;
  labels?: WeatherLabels;
}

export default function WindyMap({ className, labels }: WindyMapProps) {
  const L = resolveWeatherLabels(labels);
  const [activeTab, setActiveTab] = useState<TabValue>('temp');

  const iframeSrc = `https://embed.windy.com/embed.html?type=map&location=coordinates&metricRain=mm&metricTemp=°C&metricWind=m/s&zoom=8&overlay=${activeTab}&product=ecmwf&level=surface&lat=67.65&lon=24.5&message=true`;

  const tabLabel = (key: TabLabelKey) => L.mapTabs[key];
  const activeLabelKey = tabs.find((t) => t.value === activeTab)!.labelKey;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className={cn('w-full', className)}
    >
      <h3
        className={cn(
          'font-sans text-xl font-light mb-4',
          'text-[#1A1A2E]'
        )}
      >
        {L.mapTitle}
      </h3>

      <div className="flex flex-wrap gap-2 mb-4">
        {tabs.map((tab) => (
          <button
            key={tab.value}
            onClick={() => setActiveTab(tab.value)}
            className={cn(
              'px-3 py-1.5 rounded-lg font-sans text-sm font-medium transition-colors',
              activeTab === tab.value
                ? 'bg-[#9B7B3A] text-white'
                : 'bg-[#F0EDE5] text-[#5A5A6E] hover:text-[#1A1A2E]'
            )}
          >
            {tabLabel(tab.labelKey)}
          </button>
        ))}
      </div>

      <div className="w-full rounded-xl overflow-hidden">
        <iframe
          key={activeTab}
          src={iframeSrc}
          title={L.mapIframeTitle.replace('{overlay}', tabLabel(activeLabelKey))}
          loading="lazy"
          className="w-full h-[300px] md:h-[450px] border-0"
          allowFullScreen
        />
      </div>
    </motion.div>
  );
}
